import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSharedReport } from "@/lib/sharedInsightApi";
import { shouldRetrySharedRecordDetailQuery } from "@/lib/record-detail-transport";
import type {
  TranscriptUtterance,
  DetailedReportResponse,
  RecordMetaResponse,
} from "@/lib/types";
import { normalizeMarkdownValue } from "@/lib/markdown-normalizer";
import { hasProperty } from "@/utils/type-guards";

// НОВЫЕ КЛЮЧИ КЕШИРОВАНИЯ ДЛЯ SHARED ЗАПИСЕЙ - MARKDOWN ТЕПЕРЬ В DETAILS
export const sharedRecordsKeys = {
  all: ["shared-records"] as const,
  details: () => [...sharedRecordsKeys.all, "details"] as const,
  detail: (id: string, version?: number | null) =>
    [...sharedRecordsKeys.details(), id, version || "latest"] as const,
  // УДАЛИТЬ: qa, persona, jtbd, custom ключи
};

// ОБНОВЛЕННЫЙ useSharedRecordDetails - ЕДИНСТВЕННЫЙ ИСТОЧНИК ДАННЫХ
function useSharedRecordDetailsBase<TData = DetailedReportResponse>(
  recordId: string,
  version?: number | null,
  options?: {
    enabled?: boolean;
    select?: (data: DetailedReportResponse) => TData;
  }
): UseQueryResult<TData> {
  const enabled = (options?.enabled ?? true) && Boolean(recordId);

  return useQuery<DetailedReportResponse, Error, TData>({
    queryKey: sharedRecordsKeys.detail(recordId, version),
    queryFn: async () => {
      return await getSharedReport(recordId, version);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: shouldRetrySharedRecordDetailQuery,
    select: options?.select,
  });
}

export function useSharedRecordDetails(
  recordId: string,
  version?: number | null,
  options?: { enabled?: boolean }
) {
  return useSharedRecordDetailsBase(recordId, version, options);
}

// НОВЫЙ ХУК ДЛЯ ПОЛУЧЕНИЯ ВСЕХ SHARED MARKDOWN ОТЧЕТОВ ОДНИМ ЗАПРОСОМ
const EMPTY_MARKDOWN: Pick<
  DetailedReportResponse,
  "qa_report" | "persona_report" | "custom_report" | "jtbd_markdown_report"
> = {
  qa_report: null,
  persona_report: null,
  custom_report: null,
  jtbd_markdown_report: null,
};

export interface UseSharedMarkdownReportsOptions {
  enabled?: boolean;
}

export function useSharedMarkdownReports(
  recordId: string,
  version?: number | null,
  options?: UseSharedMarkdownReportsOptions
) {
  const isEnabled = (options?.enabled ?? true) && Boolean(recordId);
  const {
    data: recordDetails = EMPTY_MARKDOWN,
    isLoading,
    error,
  } = useSharedRecordDetails(recordId, version, { enabled: isEnabled });

  const normalizedQaReport = normalizeMarkdownValue(recordDetails.qa_report);
  const normalizedPersonaReport = normalizeMarkdownValue(
    recordDetails.persona_report
  );
  const normalizedCustomReport = normalizeMarkdownValue(
    recordDetails.custom_report
  );
  const normalizedJtbdReport = normalizeMarkdownValue(
    recordDetails.jtbd_markdown_report
  );

  return {
    qaReport: normalizedQaReport ?? EMPTY_MARKDOWN.qa_report,
    personaReport: normalizedPersonaReport ?? EMPTY_MARKDOWN.persona_report,
    customReport: normalizedCustomReport ?? EMPTY_MARKDOWN.custom_report,
    jtbdMarkdownReport:
      normalizedJtbdReport ?? EMPTY_MARKDOWN.jtbd_markdown_report,
    isLoading,
    error,
    recordDetails, // для отладки
  };
}

// Hook for fetching shared transcript data - теперь использует detail ключ
export function useSharedTranscript(
  recordId: string,
  version?: number | null,
  options?: { enabled?: boolean }
) {
  return useSharedRecordDetailsBase<TranscriptUtterance[]>(recordId, version, {
    enabled: options?.enabled,
    select: (data) => data.transcripts ?? [],
  });
}

// Hook for fetching shared setup data - теперь использует detail ключ
export function useSharedSetupData(
  recordId: string,
  version?: number | null,
  options?: { enabled?: boolean }
) {
  return useSharedRecordDetailsBase(recordId, version, {
    enabled: options?.enabled,
    select: (data) => data.setup ?? null,
  });
}

export function useSharedRecordMeta(
  recordId: string,
  options?: { enabled?: boolean }
): UseQueryResult<RecordMetaResponse, Error> {
  // Reuse the existing detail query to avoid duplicate fetches
  return useSharedRecordDetailsBase<RecordMetaResponse>(recordId, undefined, {
    enabled: options?.enabled,
    select: (data) => ({
      id: data.id,
      name: data.name,
      status: data.status,
      available_versions: data.available_versions,
      size_mb: data.size_mb,
      file_extension: data.file_extension,
      is_public: data.is_public,
      created_at: hasProperty(data, "created_at") && typeof data.created_at === "string"
        ? data.created_at
        : new Date().toISOString(),
      updated_at: hasProperty(data, "updated_at") && typeof data.updated_at === "string"
        ? data.updated_at
        : new Date().toISOString(),
      participants_count: hasProperty(data, "participants_count") && typeof data.participants_count === "number"
        ? data.participants_count
        : undefined,
    } as RecordMetaResponse),
  });
}
